import React, { useState } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button, Form, Input } from 'antd';
import { PlusOutlined, DeleteFilled, CopyOutlined, EyeOutlined } from '@ant-design/icons';

import { ReportDistributionFrequency } from 'shared/services/prisma/types';
import { EDIT_REPORT } from 'shared/constants/permissions';
import useCustomer from 'hooks/useCustomer';
import useGlobalContext from 'hooks/useGlobalContext';

import Loading from 'components/Loading';
import Page from 'components/Page';
import Table from 'components/Table';
import { renderCustomer } from 'components/Table/renderers';
import LanguageTag from 'components/LanguageTag';
import Drawer from 'components/Drawer';

import globalMessages from 'translations/messages';
import messages from '../messages';
import './reports.less';

const REPORTS_QUERY = gql`
  query Reports_GetReports($customerIds: [ID!]) {
    reports(customerIds: $customerIds) {
      id
      title
      language
      languageName
      distributions {
        id
        frequency
        recipients
        includePDF
        startDateTime
        endDateTime
        deliveries(orderBy:date_DESC, first: 1) {
          date
          id
        }
        nextDeliveryDate
      }
      content {
        id
      }
      customer {
        id
        name
      }
    }
  }
`;

const CREATE_REPORT = gql`
  mutation Reports_CreateReport($title: String!, $customerId: ID!) {
    created: createReport(title: $title, customerId: $customerId) {
      id
      title
    }
  }
`;

const DELETE_REPORT = gql`
  mutation Reports_DeleteReport($id: ID!) {
    deleted: deleteReport(id: $id) {
      id
    }
  }
`;

const DUPLICATE_REPORT = gql`
  mutation Reports_DuplicateReport($id: ID!) {
    duplicated: duplicateReport(id: $id) {
      id
    }
  }
`;

const frequencies = Object.entries(ReportDistributionFrequency).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const Reports = () => {
  const { formatMessage: $t } = useIntl();
  const { customer, hasCustomerPermission, loading: customerLoading } = useCustomer();
  const [search, setSearch] = useState();
  const [createDrawerIsOpen, setCreateDrawerIsOpen] = useState(false);
  const openCreateDrawer = () => setCreateDrawerIsOpen(true);
  const closeCreateDrawer = () => setCreateDrawerIsOpen(false);

  const customerId = customer?.id;

  const { loading, data: { reports = [] } = {}, refetch } = useQuery(REPORTS_QUERY, {
    variables: { customerIds: customer ? [customer.id] : [] },
  });

  const [createReportForm] = Form.useForm();
  const [createReport, { loading: creating }] = useMutation(CREATE_REPORT);
  const [deleteReport] = useMutation(DELETE_REPORT);
  const [duplicateReport] = useMutation(DUPLICATE_REPORT);
  const { modal, message } = useGlobalContext();

  if (customerLoading || loading) return <Loading />;

  const onSubmitForm = async (values) => {
    try {
      // useMutation hook does have onCompleted and onError options but they will never run.
      // https://github.com/trojanowski/@apollo/client/react/hoc-hooks/issues/20#issuecomment-440040136
      const { data: { created } } = await createReport({
        variables: { title: values.title, customerId },
      });

      refetch();

      closeCreateDrawer();
      message.success($t(messages.createReportSuccess, { title: created.title }));
    } catch (e) {
      console.error(e);
      message.error($t(messages.createReportFail));
    }
  };

  const doDelete = async (report) => {
    try {
      await deleteReport({
        variables: { id: report.id },
      });
      refetch();

      message.success($t(messages.deleteReportSuccess, { title: report.title }));
    } catch (e) {
      console.error(e);
      message.error($t(messages.deleteReportFail));
    }
  };

  const onDelete = async (report) => {
    modal.confirm({
      title: $t(messages.confirmDeleteReport, { title: report.title }),
      content: $t(messages.confirmDeleteReportDesc),
      okType: 'danger',
      onOk() { doDelete(report); },
    });
  };

  const onDuplicate = async (report) => {
    try {
      await duplicateReport({
        variables: { id: report.id },
      });
      refetch();

      message.success($t(messages.duplicateReportSuccess, { title: report.title }));
    } catch (e) {
      console.error(e);
      message.error($t(messages.duplicateReportFail));
    }
  };

  const tableData = reports.map((report) => {
    const distribution = report.distributions[0] || {};
    const recipients = distribution.recipients || [];
    return {
      ...report,
      recipientCount: recipients.length,
      graphCount: report.content.length,
      frequency: frequencies[distribution.frequency],
      latestDeliveryDate: distribution.deliveries?.length ? distribution.deliveries[0].date : null,
      nextDeliveryDate: distribution.nextDeliveryDate,
      includePDF: distribution.includePDF,
    };
  });

  const actionsMenuItems = record => [{
    key: 'preview',
    icon: <EyeOutlined />,
    label: (
      <Link to={`/reports/${record.id}`}>
        <FormattedMessage {...messages.preview} />
      </Link>
    ),
  }, {
    key: 'duplicate',
    icon: <CopyOutlined />,
    label: <FormattedMessage {...globalMessages.duplicate} />,
    onClick: () => onDuplicate(record),
    visible: hasCustomerPermission(EDIT_REPORT),
  }, {
    key: 'delete',
    icon: <DeleteFilled />,
    label: <FormattedMessage {...globalMessages.delete} />,
    onClick: () => onDelete(record),
    visible: hasCustomerPermission(EDIT_REPORT),
    danger: true,
  }];

  const columns = [
    {
      dataIndex: 'customer',
      title: <FormattedMessage {...globalMessages.customer} />,
      render: renderCustomer,
      sorter: (a, b) => a.customer.name.localeCompare(b.customer.name),
      search: (cus, s) => cus.name.toLowerCase().search(s) !== -1,
      inactive: customer,
    },
    {
      dataIndex: 'title',
      title: <FormattedMessage {...messages.reportColumnTitle} />,
      render: (title, record) => (hasCustomerPermission(EDIT_REPORT) ? <Link to={`reports/${record.id}/edit/settings`}>{title}</Link> : title),
      defaultSortOrder: 'ascend',
    },
    {
      dataIndex: 'language',
      title: <FormattedMessage {...messages.languageColumnTitle} />,
      render: (_, record) => <LanguageTag {...record} />,
      sorter: (a, b) => a.languageName.localeCompare(b.languageName),
      search: (language, s, { languageName }) => (languageName && languageName.toLowerCase().search(s) !== -1) || (language && language.toLowerCase().search(s) !== -1),
    },
    {
      dataIndex: 'frequency',
      title: <FormattedMessage {...messages.frequency} />,
    },
    {
      dataIndex: 'latestDeliveryDate',
      title: <FormattedMessage {...messages.latestDeliveryDate} />,
      type: 'date',
    },
    {
      dataIndex: 'nextDeliveryDate',
      title: <FormattedMessage {...messages.nextDeliveryDate} />,
      type: 'date',
    },
    {
      dataIndex: 'recipientCount',
      title: <FormattedMessage {...messages.recipients} />,
      type: 'number',
    },
    {
      dataIndex: 'graphCount',
      title: <FormattedMessage {...messages.graphCount} />,
      type: 'number',
    },
    {
      dataIndex: 'includePDF',
      title: <FormattedMessage {...messages.pdf} />,
      type: 'bool',
    },
  ];

  function renderCreateReportDrawer() {
    return (
      <Drawer
        title={<FormattedMessage {...messages.createReport} />}
        open={createDrawerIsOpen}
        onClose={closeCreateDrawer}
        onCancel={closeCreateDrawer}
        onOk={createReportForm.submit}
        confirmLoading={creating}
      >
        <Form form={createReportForm} onFinish={onSubmitForm} layout="vertical">
          <Form.Item
            name="title"
            label={<FormattedMessage {...messages.reportTitleInputLabel} />}
            rules={[{ required: true, message: $t(messages.reportTitleInputRequired) }]}
          >
            <Input placeholder={$t(messages.reportTitleInputPlaceholder)} />
          </Form.Item>
        </Form>
      </Drawer>
    );
  }

  return (
    <Page>
      {renderCreateReportDrawer()}
      <Page.Header
        title={<FormattedMessage {...globalMessages.reports} />}
        extra={(
          <React.Fragment key="1">
            <Input.Search onSearch={setSearch} allowClear />
            {hasCustomerPermission(EDIT_REPORT)
              && (
                <Button onClick={openCreateDrawer} type="primary" disabled={!customer}>
                  <PlusOutlined />
                  <FormattedMessage {...messages.newReport} />
                </Button>
              )}
          </React.Fragment>
        )}
      />
      <Page.Content>
        <Table
          emptyMessage={<FormattedMessage {...(customer ? messages.empty : globalMessages.selectCustomer)} />}
          columns={columns}
          dataSource={tableData}
          actionsMenuItems={actionsMenuItems}
          search={search}
        />
      </Page.Content>
    </Page>
  );
};

export default Reports;
